import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveRichText } from '@vibesites/runtime';
import {
  useEditMode,
  useSlotGroup,
  useVibesites,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons/faUser';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useCtaColorVars } from '../hooks/useCtaColorVars';

// ============================================================================
// Meet The Team — Member Cards
// ---------------------------------------------------------------------------
// Three editable team-member cards (photo, name, title, short bio) rendered
// in a responsive grid: stacked on mobile, 2-up on tablet, 3-up on desktop.
// Each card is its own slot group so editors can swap photo, retitle, or
// rewrite the bio independently. Visually matches the LifestyleResidentTypes
// card treatment (surface background + Oswald title + Space Mono body + subtle
// hover lift) so the page feels native to the theme.
//
// Name, title, bio, and photo default to empty. A card with none of them does
// not render on the live site, and the whole section is skipped when every card
// is empty. In edit mode empty cards still render (dimmed, with a notice) so
// operators can reach the editor to fill them in. Mirrors Avenue's TeamMembers.
//
// These groups intentionally have no `sourceBinding`: name, title, and bio are
// independent of the photo, so the `slot-group-missing-source-binding` warning
// is expected.
// ============================================================================

const member1Def: SlotGroupDefinition = {
  label: 'Team Member 1',
  fields: {
    photo: {
      label: 'Photo',
      type: 'image',
      defaultValue: '',
    },
    photo_alt: {
      label: 'Photo Alt Text',
      type: 'text',
      defaultValue: '',
      generator: {
        prompt:
          'Alt text for the photo currently set on this card: a short, literal description of what the image shows (the person, their setting, what they are doing). Include the team member\'s name and role when known. Do not start with "Image of" or "Photo of". Leave empty if the card has no photo.',
        context: 'Portrait above a team member\'s name, title, and bio on the Meet The Team page.',
        length: 'under 125 characters',
      },
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: '',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: '',
    },
    bio: {
      label: 'Bio',
      type: 'richtext',
      defaultValue: '',
    },
  },
  generator: {
    prompt:
      'A real on-site Leasing Professional at this property — first name + last initial (e.g. "Justiana M."), a warm 1-2 sentence bio that may mention a hobby or interest, and a friendly professional portrait if one exists in the gallery (otherwise leave the photo empty so the blank placeholder shows). Whenever the card has a photo — picked here or already set by an editor — write `photo_alt` describing what that photo actually shows; leave `photo_alt` empty when there is no photo. Leave `title` alone.',
    context:
      'Card 1 of 3 on the Meet The Team page. Bio and photo must feel distinct from cards 2 (Property Manager) and 3 (Maintenance Supervisor). If this property has no Leasing Professional on record, leave the name, bio, and photo empty so the card stays hidden on the live site.',
  },
};

const member2Def: SlotGroupDefinition = {
  label: 'Team Member 2',
  fields: {
    photo: {
      label: 'Photo',
      type: 'image',
      defaultValue: '',
    },
    photo_alt: {
      label: 'Photo Alt Text',
      type: 'text',
      defaultValue: '',
      generator: {
        prompt:
          'Alt text for the photo currently set on this card: a short, literal description of what the image shows (the person, their setting, what they are doing). Include the team member\'s name and role when known. Do not start with "Image of" or "Photo of". Leave empty if the card has no photo.',
        context: 'Portrait above a team member\'s name, title, and bio on the Meet The Team page.',
        length: 'under 125 characters',
      },
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: '',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: '',
    },
    bio: {
      label: 'Bio',
      type: 'richtext',
      defaultValue: '',
    },
  },
  generator: {
    prompt:
      'A real on-site Property Manager at this property — first name + last initial (e.g. "Mohammed M."), a warm 1-2 sentence bio that may reference the property by name or mention a hobby, and a friendly professional portrait if one exists in the gallery (otherwise leave the photo empty so the blank placeholder shows). Whenever the card has a photo — picked here or already set by an editor — write `photo_alt` describing what that photo actually shows; leave `photo_alt` empty when there is no photo. Leave `title` alone.',
    context:
      'Card 2 of 3 on the Meet The Team page. Bio and photo must feel distinct from cards 1 (Leasing Professional) and 3 (Maintenance Supervisor). If this property has no Property Manager on record, leave the name, bio, and photo empty so the card stays hidden on the live site.',
  },
};

const member3Def: SlotGroupDefinition = {
  label: 'Team Member 3',
  fields: {
    photo: {
      label: 'Photo',
      type: 'image',
      defaultValue: '',
    },
    photo_alt: {
      label: 'Photo Alt Text',
      type: 'text',
      defaultValue: '',
      generator: {
        prompt:
          'Alt text for the photo currently set on this card: a short, literal description of what the image shows (the person, their setting, what they are doing). Include the team member\'s name and role when known. Do not start with "Image of" or "Photo of". Leave empty if the card has no photo.',
        context: 'Portrait above a team member\'s name, title, and bio on the Meet The Team page.',
        length: 'under 125 characters',
      },
    },
    name: {
      label: 'Name',
      type: 'text',
      defaultValue: '',
    },
    title: {
      label: 'Title',
      type: 'text',
      defaultValue: '',
    },
    bio: {
      label: 'Bio',
      type: 'richtext',
      defaultValue: '',
    },
  },
  generator: {
    prompt:
      'A real on-site Maintenance Supervisor at this property — first name + last initial (e.g. "Aws H."), a warm 1-2 sentence bio that may mention a hobby or interest, and a friendly professional portrait if one exists in the gallery (otherwise leave the photo empty so the blank placeholder shows). Whenever the card has a photo — picked here or already set by an editor — write `photo_alt` describing what that photo actually shows; leave `photo_alt` empty when there is no photo. Leave `title` alone.',
    context:
      'Card 3 of 3 on the Meet The Team page. Bio and photo must feel distinct from cards 1 (Leasing Professional) and 2 (Property Manager). If this property has no Maintenance Supervisor on record, leave the name, bio, and photo empty so the card stays hidden on the live site.',
  },
};

// ============================================================================
// Helpers
// ============================================================================

function hasText(html: string | null | undefined): boolean {
  return (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim() !== '';
}

function hasMemberContent(member: Record<string, any>): boolean {
  return (
    hasText(member.photo) || hasText(member.name) || hasText(member.title) || hasText(member.bio)
  );
}

function HiddenCardNotice() {
  return (
    <div className="mttm-hidden-notice">
      <p>Hidden on the live site until a photo, name, title, or bio is added.</p>
    </div>
  );
}

// ============================================================================
// Reusable member card renderer
// ============================================================================

interface TeamMemberCardProps {
  path: string;
  definition: SlotGroupDefinition;
  index: number;
}

function TeamMemberCard({ path, definition, index }: TeamMemberCardProps) {
  const member = useSlotGroup(path, definition);
  const ref = useScrollReveal<HTMLDivElement>();
  const isEditMode = useEditMode()?.isEditMode ?? false;

  const { property } = useVibesites();
  const bioHtml = resolveRichText(member.bio || '', property as Record<string, any>);
  const hasBio = hasText(bioHtml);
  const hasName = !!member.name?.trim();
  const hasTitle = !!member.title?.trim();

  const visible = hasMemberContent(member);
  if (!visible && !isEditMode) return null;

  const initials = (member.name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part[0])
    .join('')
    .toUpperCase();

  return (
    <EditableSlotGroup groupPath={path} definition={definition} className="mttm-card-cell">
      <article
        ref={ref}
        className={`reveal-fade-up mttm-card${!visible ? ' mttm-card--hidden' : ''}`}
        style={{ transitionDelay: `${index * 90}ms` }}
      >
        {!visible && <HiddenCardNotice />}
        <div className="mttm-photo-wrap">
          {member.photo ? (
            <VibesitesImage
              url={member.photo}
              alt={member.photo_alt || `${member.name || 'Team member'} portrait`}
              sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
              className="mttm-photo"
            />
          ) : (
            <div className="mttm-photo mttm-photo-placeholder" aria-hidden="true">
              {hasName && initials ? (
                <span>{initials}</span>
              ) : (
                <FontAwesomeIcon icon={faUser} className="mttm-placeholder-icon" />
              )}
            </div>
          )}
        </div>
        <div className="mttm-card-body">
          {hasName ? (
            <h3 className="mttm-name">{member.name}</h3>
          ) : (
            <div className="mttm-placeholder-line mttm-placeholder-name" aria-hidden="true" />
          )}
          {hasTitle ? <p className="mttm-title">{member.title}</p> : null}
          {hasBio ? (
            <div
              className="mttm-bio"
              data-vibesite-richtext=""
              dangerouslySetInnerHTML={{ __html: bioHtml }}
            />
          ) : (
            <div className="mttm-placeholder-bio" aria-hidden="true">
              <div className="mttm-placeholder-line" />
              <div className="mttm-placeholder-line" style={{ width: '90%' }} />
              <div className="mttm-placeholder-line" style={{ width: '65%' }} />
            </div>
          )}
        </div>
      </article>
    </EditableSlotGroup>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function MeetTheTeamMembers() {
  useRevealStyles();
  const ctaColorVars = useCtaColorVars();
  const isEditMode = useEditMode()?.isEditMode ?? false;
  const member1 = useSlotGroup('meettheteammembers.member_1', member1Def);
  const member2 = useSlotGroup('meettheteammembers.member_2', member2Def);
  const member3 = useSlotGroup('meettheteammembers.member_3', member3Def);

  const members: { path: string; def: SlotGroupDefinition }[] = [
    { path: 'meettheteammembers.member_1', def: member1Def },
    { path: 'meettheteammembers.member_2', def: member2Def },
    { path: 'meettheteammembers.member_3', def: member3Def },
  ];

  if (!isEditMode && ![member1, member2, member3].some(hasMemberContent)) return null;

  return (
    <section className="mttm-section" style={ctaColorVars}>
      <div className="mttm-container">
        <div className="mttm-grid">
          {members.map((member, i) => (
            <TeamMemberCard
              key={member.path}
              path={member.path}
              definition={member.def}
              index={i}
            />
          ))}
        </div>

        {/* Schema-lint anchors — hidden, literal paths for each card so the
            source-level linter can match `slot-group-missing-editable-wrapper`.
            Same pattern as LifestyleResidentTypes. */}
        <EditableSlotGroup
          groupPath="meettheteammembers.member_1"
          definition={member1Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="meettheteammembers.member_2"
          definition={member2Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup
          groupPath="meettheteammembers.member_3"
          definition={member3Def}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
      </div>

      <style>{`
        .mttm-section {
          position: relative;
          background: var(--vibe-color-surface, #f4f2ee);
          padding: 3.5rem 1.25rem 4.5rem;
        }
        @media (min-width: 768px) {
          .mttm-section { padding: 4.5rem 1.5rem 5.5rem; }
        }

        .mttm-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        /* ── Grid ── */
        .mttm-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.75rem;
        }
        @media (min-width: 640px) {
          .mttm-grid { grid-template-columns: 1fr 1fr; }
        }
        @media (min-width: 1024px) {
          .mttm-grid { grid-template-columns: repeat(3, 1fr); gap: 2rem; }
        }

        /* The editable wrapper is the grid item; the card must fill it so
           every card in a row matches the tallest one. */
        .mttm-card-cell {
          display: flex;
          flex-direction: column;
        }
        .mttm-card-cell > * {
          flex: 1 1 auto;
        }

        .mttm-card {
          position: relative;
          display: flex;
          flex-direction: column;
          background: #ffffff;
          border: 1px solid rgba(17, 24, 39, 0.06);
          box-shadow: 0 4px 20px -12px rgba(0, 0, 0, 0.2);
          overflow: hidden;
          transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1),
                      box-shadow 0.4s ease,
                      border-color 0.4s ease;
        }
        .mttm-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 36px -16px rgba(0, 0, 0, 0.22);
          border-color: var(--vibe-color-secondary);
        }
        .mttm-card::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          height: 3px;
          background: var(--cta-bg, var(--vibe-color-secondary, #3E441D));
          transform: scaleX(0);
          transform-origin: left center;
          transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .mttm-card:hover::after {
          transform: scaleX(1);
        }

        /* ── Photo ── */
        .mttm-photo-wrap {
          position: relative;
          width: 100%;
          aspect-ratio: 4 / 5;
          overflow: hidden;
          background: color-mix(in srgb, var(--vibe-color-primary) 8%, #ffffff);
        }
        .mttm-photo {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center top;
          display: block;
          transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .mttm-card:hover .mttm-photo {
          transform: scale(1.04);
        }
        .mttm-photo-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--vibe-color-secondary, #3E441D);
          font-family: var(--vibe-font-heading);
          font-size: clamp(2.5rem, 6vw, 4rem);
          letter-spacing: 0.08em;
          background: color-mix(in srgb, var(--vibe-color-primary) 12%, #ffffff);
        }
        .mttm-placeholder-icon {
          width: 35%;
          height: 35%;
          color: color-mix(in srgb, var(--vibe-color-secondary, #3E441D) 35%, #ffffff);
        }
        .mttm-placeholder-line {
          height: 0.75rem;
          width: 100%;
          border-radius: 2px;
          background: color-mix(in srgb, var(--vibe-color-text, #111827) 8%, #ffffff);
        }
        .mttm-placeholder-name {
          height: 1.5rem;
          width: 10rem;
          max-width: 70%;
        }
        .mttm-card--hidden {
          opacity: 0.55;
          outline: 2px dashed color-mix(in srgb, var(--vibe-color-secondary, #3E441D) 60%, transparent);
          outline-offset: -2px;
        }
        .mttm-hidden-notice {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          z-index: 2;
          background: #fef3c7;
          color: #78350f;
          padding: 0.6rem 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.7rem;
          font-weight: 700;
          text-align: center;
          border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        }
        .mttm-hidden-notice p { margin: 0; }
        .mttm-placeholder-bio {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          margin-top: 0.25rem;
        }

        /* ── Body ── */
        .mttm-card-body {
          padding: 1.75rem 1.5rem 2rem;
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
          flex: 1 1 auto;
        }

        .mttm-name {
          margin: 0;
          font-family: var(--vibe-font-heading);
          font-weight: 400;
          font-size: 1.4rem;
          line-height: 1.15;
          color: var(--vibe-color-secondary, #3E441D);
          letter-spacing: 0.02em;
        }

        .mttm-title {
          margin: 0;
          font-family: var(--vibe-font-body);
          font-weight: 400;
          font-size: 0.78rem;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--cta-bg, var(--vibe-color-primary));
        }

        .mttm-bio,
        .mttm-bio * {
          font-family: var(--vibe-font-body);
          font-weight: 400;
        }
        .mttm-bio {
          margin: 0.25rem 0 0;
          font-size: 0.9rem;
          line-height: 1.6;
          color: rgba(17, 24, 39, 0.78);
        }
        .mttm-bio p { margin: 0 0 0.5rem; }
        .mttm-bio p:last-child { margin-bottom: 0; }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'MeetTheTeamMembers',
  displayName: 'Meet The Team — Members',
  tokens: {},
  slotGroups: {
    'meettheteammembers.member_1': member1Def,
    'meettheteammembers.member_2': member2Def,
    'meettheteammembers.member_3': member3Def,
  },
};

(MeetTheTeamMembers as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default MeetTheTeamMembers;
